/**
 * `tds.*` — read-only Form-26Q preparation surface (V1.0).
 *
 * system-decisions §9: TDS is computed at payment time, not bill time. The
 * wire surface deliberately has no `markDeposited` / `generateChallan281` /
 * `pushToTraces` mutations in V1 — those land in V1.x once the cash-movement
 * vertical ships. The SPA renders the V1.x chrome (deposit timeline, Form
 * 16A PDF, LDC roster) as disabled with a "Lands in V1.x" tooltip.
 *
 * Single procedure: `getQuarterSummary({ fy, quarter })` returns per-section
 * roll-ups + a recent-deductions feed + a Form-16A roster. Two KPIs are
 * real (quarter TDS, FY YTD); two are V1.x (pending deposit, on-time score).
 *
 * Mirror of `procedures/gst-returns.ts` — same shape, same conventions.
 */
import { z } from "zod";
import { moneyPaise, isoDate } from "../_common.js";

// ── inputs ──────────────────────────────────────────────────────────

/**
 * Indian-FY quarter input. `fy` is the starting calendar year (FY 26-27 →
 * `fy: 2026`). Range guards mirror the domain — pre-GST (<2017) is out of
 * scope; far-future is bounded just to keep the input sane.
 */
export const getQuarterSummaryInput = z.object({
  fy: z.number().int().min(2017).max(2100),
  quarter: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
});

// ── outputs ─────────────────────────────────────────────────────────

/**
 * Section codes the SPA can colour-coordinate against the prototype's
 * `b-194c` / `b-194j` / `b-194i` / `b-194q` palette. Anything we don't
 * recognise routes to `'other'` so the chart still adds up.
 */
const tdsSectionCode = z.enum(["194C", "194J", "194Q", "194I", "194H", "other"]);

const tdsBreakdownRow = z.object({
  code: tdsSectionCode,
  label: z.string(),
  hint: z.string(),
  vendorCount: z.number().int().nonnegative(),
  paymentCount: z.number().int().nonnegative(),
  tdsPaise: moneyPaise,
  grossPaise: moneyPaise,
});

const tdsDeductionRow = z.object({
  id: z.string().uuid(),
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  /** `null` when the row had an unrecognised / missing section snapshot. */
  tdsSection: z.string().nullable(),
  /** Snapshot basis-points rate at payment time. */
  tdsRateBp: z.number().int().nullable(),
  paidOn: isoDate,
  grossPaise: moneyPaise,
  tdsPaise: moneyPaise,
  mode: z.string(),
  reference: z.string().nullable(),
});

const form16aVendorRow = z.object({
  vendorId: z.string().uuid(),
  vendorName: z.string(),
  /** Section labels — may be a single label or multiple (rare). */
  sections: z.array(z.string()),
  tdsPaise: moneyPaise,
  paymentCount: z.number().int().nonnegative(),
});

/**
 * Per-month deposit projection — one row per calendar month inside the
 * quarter. Drives the ITNS-281 monthly-deposit timeline. The `dueOn`
 * date is the 7th of the next month (statutory deadline); paise is the
 * actual sum of TDS deducted in that month (NOT a naive split), so the
 * timeline reflects reality even when deductions cluster in one month.
 */
const monthlyDepositRow = z.object({
  /** `YYYY-MM` of the deduction month. */
  month: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  /** ISO date the deposit is due (7th of the following month). */
  dueOn: isoDate,
  tdsPaise: moneyPaise,
  paymentCount: z.number().int().nonnegative(),
});

export const getQuarterSummaryOutput = z.object({
  fy: z.number().int(),
  quarter: z.union([z.literal(1), z.literal(2), z.literal(3), z.literal(4)]),
  periodStart: isoDate,
  periodEnd: isoDate,
  return26qDueOn: isoDate,
  form16aDueOn: isoDate,
  company: z.object({
    legalName: z.string(),
    pan: z.string().nullable(),
    /** TAN — null until the `companies.tan` column lands (V1.x). */
    tan: z.string().nullable(),
  }),
  breakdown: z.array(tdsBreakdownRow),
  recentDeductions: z.array(tdsDeductionRow),
  form16aRoster: z.array(form16aVendorRow),
  /** Always 3 rows (one per quarter month). Real per-month TDS sums — never naive averages. */
  monthlyDeposits: z.array(monthlyDepositRow),
  totals: z.object({
    quarterTdsPaise: moneyPaise,
    fyTdsPaise: moneyPaise,
    vendorCount: z.number().int().nonnegative(),
    paymentCount: z.number().int().nonnegative(),
  }),
  /** V1.x feature flags — the SPA renders matching chrome as disabled. */
  unsupportedFeatures: z.array(z.string()).readonly(),
});

// ── derived TS types ──────────────────────────────────────────────
export type GetQuarterSummaryInput = z.infer<typeof getQuarterSummaryInput>;
export type GetQuarterSummaryOutput = z.infer<typeof getQuarterSummaryOutput>;
export type TdsSectionCode = z.infer<typeof tdsSectionCode>;
export type TdsBreakdownRow = z.infer<typeof tdsBreakdownRow>;
export type TdsDeductionRow = z.infer<typeof tdsDeductionRow>;
export type Form16AVendorRow = z.infer<typeof form16aVendorRow>;
